import { View, Text, styled, ScrollView, useTheme } from 'tamagui'
import { useState, useRef } from 'react'
import { useAppStore, useServerStore } from '@/store'

const Container = styled(View, {
  flex: 1,
  flexDirection: 'column',
})

const Header = styled(View, {
  paddingHorizontal: 32,
  paddingVertical: 20,
  borderBottomWidth: 1,
  borderBottomColor: '$borderDefault',
})

const Title = styled(Text, {
  fontFamily: '$fontHeading',
  fontWeight: '600',
  fontSize: 20,
  color: '$surfaceForeground',
})

const Body = styled(View, {
  padding: 32,
  gap: 24,
  maxWidth: 600,
})

const FormGroup = styled(View, {
  gap: 6,
})

const Label = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 12,
  fontWeight: '600',
  color: '$color8',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
})

const ColorPicker = styled(View, {
  flexDirection: 'row',
  gap: 8,
  flexWrap: 'wrap',
})

const ColorDot = styled(View, {
  width: 32,
  height: 32,
  borderRadius: '$borderRadius2xl',
  cursor: 'pointer',
  alignItems: 'center',
  justifyContent: 'center',
})

const SaveButton = styled(View, {
  height: 36,
  paddingHorizontal: 24,
  borderRadius: '$borderRadiusMd',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '$accent',
  cursor: 'pointer',
  alignSelf: 'flex-start',
  hoverStyle: { opacity: 0.9 },
})

const ServerIconWrapper = styled(View, {
  position: 'relative',
  width: 80,
  height: 80,
  borderRadius: '$borderRadiusXl',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  overflow: 'hidden',
})

const IconOverlay = styled(View, {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.5)',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: 0,
  hoverStyle: { opacity: 1 },
})

const colorOptions = ['#3ECFCF', '#E8A840', '#CF3E4F', '#A855F7', '#3ECF7B', '#3B82F6', '#EC4899', '#8B95A8']

export function OverviewSettings() {
  const { activeServerId } = useAppStore()
  const server = useServerStore((s) => activeServerId ? s.servers[activeServerId] : undefined)
  const updateServer = useServerStore((s) => s.updateServer)
  const theme = useTheme()

  const [name, setName] = useState(server?.name || '')
  const [color, setColor] = useState(server?.iconColor || colorOptions[0])
  const [description, setDescription] = useState('')
  const [iconPreview, setIconPreview] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  if (!server || !activeServerId) return null

  const handleSave = () => {
    const initials = name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
    updateServer(activeServerId, { name: name.trim(), iconColor: color, iconText: initials })
  }

  const handleIconClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const url = URL.createObjectURL(file)
      setIconPreview(url)
    }
  }

  return (
    <Container>
      <Header>
        <Title>Server Overview</Title>
      </Header>
      <ScrollView flex={1}>
        <Body>
          <FormGroup>
            <Label>Server Icon</Label>
            <ServerIconWrapper backgroundColor={color} onPress={handleIconClick}>
              {iconPreview ? (
                <img
                  src={iconPreview}
                  alt="Server icon preview"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <Text fontFamily="$fontHeading" fontWeight="700" fontSize={28} color="$surface">
                  {server.iconText}
                </Text>
              )}
              <IconOverlay>
                <Text fontFamily="$fontBody" fontSize={10} fontWeight="600" color="white" textAlign="center">
                  Change{'\n'}Icon
                </Text>
              </IconOverlay>
            </ServerIconWrapper>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
          </FormGroup>

          <FormGroup>
            <Label>Server Name</Label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={{
                background: 'transparent',
                border: `1px solid ${theme.borderDefault?.val || '#2A3142'}`,
                borderRadius: 6, padding: '8px 12px',
                color: theme.surfaceForeground?.val || '#E2E8F0',
                fontFamily: '$fontBody', fontSize: 14,
                outline: 'none', width: '100%', boxSizing: 'border-box',
              }}
            />
          </FormGroup>

          <FormGroup>
            <Label>Icon Color</Label>
            <ColorPicker>
              {colorOptions.map((c) => (
                <ColorDot
                  key={c}
                  backgroundColor={c}
                  onPress={() => setColor(c)}
                  borderWidth={color === c ? 2 : 0}
                  borderColor="$surfaceForeground"
                >
                  {color === c && <Text color="$surface" fontSize={14} fontWeight="700">✓</Text>}
                </ColorDot>
              ))}
            </ColorPicker>
          </FormGroup>

          <FormGroup>
            <Label>Description</Label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What's this server about?"
              rows={3}
              style={{
                background: 'transparent',
                border: `1px solid ${theme.borderDefault?.val || '#2A3142'}`,
                borderRadius: 6, padding: '8px 12px',
                color: theme.surfaceForeground?.val || '#E2E8F0',
                fontFamily: '$fontBody', fontSize: 14,
                outline: 'none', resize: 'none', width: '100%', boxSizing: 'border-box',
              }}
            />
          </FormGroup>

          <SaveButton onPress={handleSave}>
            <Text fontFamily="$fontBody" fontWeight="600" fontSize={13} color="$surface">Save Changes</Text>
          </SaveButton>
        </Body>
      </ScrollView>
    </Container>
  )
}
